import React from "react";
import { MapPin, Clock, Phone, Instagram, ArrowRight } from "lucide-react";
import { useI18n } from "../i18n";
import {
  WHATSAPP_URL,
  INSTAGRAM_URL,
  GOOGLE_MAPS_URL,
  GOOGLE_MAPS_EMBED,
} from "../constants";

export default function Contact() {
  const { t, lang } = useI18n();

  return (
    <section
      id="contact"
      className="relative py-24 md:py-32 border-t border-[#2d2622] bg-[#0a0908]"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: info */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <div className="divider-gold" />
              <span className="overline">{t.contact.overline}</span>
            </div>
            <h2
              className="font-serif-display text-4xl sm:text-5xl md:text-6xl font-light leading-tight text-[#f4f0eb]"
              data-testid="contact-title"
            >
              {t.contact.title}
            </h2>

            {/* Reserve CTA card */}
            <div
              className="mt-10 bg-gradient-to-br from-[#1a0e10] to-[#0d0c0b] border border-[#6a1a22]/40 p-8"
              data-testid="contact-reserve-card"
            >
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#d4af37] mb-3">
                WhatsApp
              </div>
              <h3 className="font-serif-display text-2xl md:text-3xl text-[#f4f0eb] font-normal">
                {t.contact.reserve_title}
              </h3>
              <p className="mt-3 text-[#a39e97] text-sm leading-relaxed">
                {t.contact.reserve_desc}
              </p>
              <a
                href={WHATSAPP_URL(
                  lang === "es"
                    ? "Hola TINTA, me gustaría hacer una reserva."
                    : "Hi TINTA, I'd like to make a reservation."
                )}
                target="_blank"
                rel="noopener noreferrer"
                className="tinta-btn-primary mt-6 w-full"
                data-testid="contact-whatsapp-button"
              >
                {t.contact.reserve_cta}
                <ArrowRight size={16} />
              </a>
              <div className="mt-4 text-[11px] uppercase tracking-[0.2em] text-[#a39e97]">
                {t.contact.note}
              </div>
            </div>

            {/* Details */}
            <dl className="mt-10 space-y-6">
              <InfoRow
                icon={<MapPin size={18} className="text-[#d4af37]" />}
                label={t.contact.address_label}
                value={
                  <a
                    href={GOOGLE_MAPS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#d4af37] transition-colors"
                    data-testid="contact-address-link"
                  >
                    {t.contact.address}
                  </a>
                }
                testid="contact-address"
              />
              <InfoRow
                icon={<Clock size={18} className="text-[#d4af37]" />}
                label={t.contact.hours_label}
                value={
                  <div className="space-y-1">
                    {t.contact.hours.map(([day, range], i) => (
                      <div
                        key={i}
                        className="flex justify-between gap-4 text-[#d9d4cd]"
                      >
                        <span>{day}</span>
                        <span className="text-[#a39e97]">{range}</span>
                      </div>
                    ))}
                  </div>
                }
                testid="contact-hours"
              />
              <InfoRow
                icon={<Phone size={18} className="text-[#d4af37]" />}
                label={t.contact.phone_label}
                value={
                  <a
                    href={`tel:+541158214406`}
                    className="hover:text-[#d4af37] transition-colors"
                    data-testid="contact-phone-link"
                  >
                    {t.contact.phone}
                  </a>
                }
                testid="contact-phone"
              />
              <InfoRow
                icon={<Instagram size={18} className="text-[#d4af37]" />}
                label={t.contact.ig_label}
                value={
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#d4af37] transition-colors"
                    data-testid="contact-instagram-link"
                  >
                    {t.contact.ig_handle}
                  </a>
                }
                testid="contact-instagram"
              />
            </dl>
          </div>

          {/* Right: Map */}
          <div className="lg:col-span-7">
            <div
              className="relative w-full h-[420px] md:h-full min-h-[420px] border border-[#2d2622] overflow-hidden"
              data-testid="contact-map"
            >
              <iframe
                title="TINTA Restaurante & Bar — Mapa"
                src={GOOGLE_MAPS_EMBED}
                className="w-full h-full grayscale-[20%] contrast-110"
                style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg) brightness(0.95)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ icon, label, value, testid }) {
  return (
    <div className="flex gap-4" data-testid={testid}>
      <div className="mt-1">{icon}</div>
      <div className="flex-1">
        <dt className="text-[10px] uppercase tracking-[0.28em] text-[#a39e97] mb-1.5">
          {label}
        </dt>
        <dd className="text-[#f4f0eb] text-[15px] leading-relaxed">{value}</dd>
      </div>
    </div>
  );
}
